'use client';

import { useState, useEffect } from 'react';
import {
    TrendingUp, TrendingDown, Wallet, CreditCard, PiggyBank, Receipt,
    ArrowUpRight, ArrowDownRight, DollarSign, BarChart3, PieChart as PieChartIcon,
} from 'lucide-react';

/* ═══ Types ═══ */
interface CategoryExpense {
    category: string;
    amount: number;
    percentage: number;
    color: string;
}

interface MonthlyBalance {
    month: string;
    income: number;
    expense: number;
    balance: number;
}

interface Transaction {
    id: string;
    date: string;
    type: '입금' | '출금';
    amount: number;
    category: string;
    description: string;
}

/* ═══ Mock Data (Real API 연동 예정) ═══ */
const CATEGORIES: CategoryExpense[] = [
    { category: '인건비', amount: 98000000, percentage: 52, color: '#6366f1' },
    { category: '외주비', amount: 42000000, percentage: 22, color: '#22c55e' },
    { category: '사무실 운영', amount: 18000000, percentage: 10, color: '#f59e0b' },
    { category: '장비/SW', amount: 12000000, percentage: 6, color: '#ef4444' },
    { category: '교통비', amount: 8000000, percentage: 4, color: '#06b6d4' },
    { category: '기타', amount: 10000000, percentage: 6, color: '#8b5cf6' },
];

const MONTHLY: MonthlyBalance[] = [
    { month: '2025-10', income: 210000000, expense: 165000000, balance: 45000000 },
    { month: '2025-11', income: 185000000, expense: 172000000, balance: 13000000 },
    { month: '2025-12', income: 240000000, expense: 180000000, balance: 60000000 },
    { month: '2026-01', income: 195000000, expense: 168000000, balance: 27000000 },
    { month: '2026-02', income: 220000000, expense: 175000000, balance: 45000000 },
    { month: '2026-03', income: 200000000, expense: 150000000, balance: 50000000 },
];

const TRANSACTIONS: Transaction[] = [
    { id: '1', date: '2026-03-15', type: '입금', amount: 85000000, category: '설계비', description: 'A프로젝트 3차 기성' },
    { id: '2', date: '2026-03-14', type: '출금', amount: 12000000, category: '외주비', description: '구조설계 외주 정산' },
    { id: '3', date: '2026-03-13', type: '입금', amount: 45000000, category: '설계비', description: '웅촌초 중도금' },
    { id: '4', date: '2026-03-12', type: '출금', amount: 38000000, category: '인건비', description: '3월 급여 지급' },
    { id: '5', date: '2026-03-11', type: '출금', amount: 5200000, category: '장비/SW', description: 'AutoCAD/BIM 라이선스' },
    { id: '6', date: '2026-03-10', type: '입금', amount: 28000000, category: '설계비', description: '대곡고 잔금' },
    { id: '7', date: '2026-03-08', type: '출금', amount: 2800000, category: '사무실', description: '사무실 임대료' },
    { id: '8', date: '2026-03-05', type: '입금', amount: 42000000, category: '감리비', description: '김해도서관 감리 기성' },
];

const fmt = (n: number) => {
    if (n >= 100000000) return `${(n / 100000000).toFixed(1)}억`;
    if (n >= 10000) return `${(n / 10000).toFixed(0)}만`;
    return n.toLocaleString();
};

const fmtFull = (n: number) => n.toLocaleString('ko-KR') + '원';

/* ═══ Component ═══ */
export default function FinancePanel({ isDark }: { isDark: boolean }) {
    const [view, setView] = useState<'overview' | 'expenses' | 'transactions'>('overview');
    const [animateIn, setAnimateIn] = useState(false);

    useEffect(() => {
        setAnimateIn(true);
    }, []);

    const bg = isDark ? 'bg-gray-900/60' : 'bg-white';
    const border = isDark ? 'border-white/10' : 'border-gray-200';
    const cardBg = isDark ? 'bg-gray-800/60' : 'bg-gray-50';
    const text1 = isDark ? 'text-white' : 'text-gray-900';
    const text2 = isDark ? 'text-gray-400' : 'text-gray-500';
    const text3 = isDark ? 'text-gray-500' : 'text-gray-400';

    const totalIncome = MONTHLY.reduce((s, m) => s + m.income, 0);
    const totalExpense = MONTHLY.reduce((s, m) => s + m.expense, 0);
    const netIncome = totalIncome - totalExpense;
    const savingsRate = ((netIncome / totalIncome) * 100).toFixed(1);
    const currentMonth = MONTHLY[MONTHLY.length - 1];
    const prevMonth = MONTHLY[MONTHLY.length - 2];
    const incomeGrowth = ((currentMonth.income - prevMonth.income) / prevMonth.income * 100).toFixed(1);

    // Pie chart SVG
    const renderPie = () => {
        let cumAngle = 0;
        const cx = 80, cy = 80, r = 70;
        return (
            <svg width="160" height="160" viewBox="0 0 160 160">
                {CATEGORIES.map((cat, i) => {
                    const angle = (cat.percentage / 100) * 360;
                    const startAngle = cumAngle;
                    cumAngle += angle;
                    const endAngle = cumAngle;
                    const startRad = (startAngle - 90) * Math.PI / 180;
                    const endRad = (endAngle - 90) * Math.PI / 180;
                    const x1 = cx + r * Math.cos(startRad);
                    const y1 = cy + r * Math.sin(startRad);
                    const x2 = cx + r * Math.cos(endRad);
                    const y2 = cy + r * Math.sin(endRad);
                    const largeArc = angle > 180 ? 1 : 0;
                    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
                    return <path key={i} d={d} fill={cat.color} opacity={0.85} className="hover:opacity-100 transition-opacity cursor-pointer" />;
                })}
                <circle cx={cx} cy={cy} r={35} fill={isDark ? '#111827' : '#f9fafb'} />
                <text x={cx} y={cy - 6} textAnchor="middle" className={`text-xs font-bold ${text1}`} fill={isDark ? '#fff' : '#111'}>총 지출</text>
                <text x={cx} y={cy + 12} textAnchor="middle" className="text-[10px]" fill={isDark ? '#9ca3af' : '#6b7280'}>{fmt(totalExpense)}</text>
            </svg>
        );
    };

    // Bar chart SVG
    const renderBars = () => {
        const maxVal = Math.max(...MONTHLY.map(m => Math.max(m.income, m.expense)));
        const barH = 120;
        const barW = 30;
        const gap = 14;
        const totalW = MONTHLY.length * (barW * 2 + gap) + gap;
        return (
            <svg width="100%" height="160" viewBox={`0 0 ${totalW} 160`} preserveAspectRatio="xMidYMid meet">
                {MONTHLY.map((m, i) => {
                    const x = i * (barW * 2 + gap) + gap;
                    const ih = (m.income / maxVal) * barH;
                    const eh = (m.expense / maxVal) * barH;
                    return (
                        <g key={i}>
                            <rect x={x} y={barH + 10 - ih} width={barW - 2} height={ih} rx={3} fill="#22c55e" opacity={0.7} />
                            <rect x={x + barW} y={barH + 10 - eh} width={barW - 2} height={eh} rx={3} fill="#ef4444" opacity={0.7} />
                            <text x={x + barW} y={barH + 28} textAnchor="middle" className="text-[9px]" fill={isDark ? '#6b7280' : '#9ca3af'}>
                                {m.month.slice(5)}월
                            </text>
                        </g>
                    );
                })}
            </svg>
        );
    };

    return (
        <div className={`flex-1 overflow-y-auto p-4 md:p-6 space-y-5 transition-all duration-300 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className={`text-xl font-bold flex items-center gap-2 ${text1}`}>
                        <Wallet className="w-5 h-5 text-indigo-400" />
                        SnapPrice Finance
                    </h2>
                    <p className={`text-sm ${text2}`}>디자인인사이트 실시간 재무 대시보드</p>
                </div>
                <div className={`flex gap-1 rounded-lg p-1 ${cardBg}`}>
                    {(['overview', 'expenses', 'transactions'] as const).map(v => (
                        <button key={v} onClick={() => setView(v)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${view === v
                                ? 'bg-indigo-500 text-white shadow-sm'
                                : `${text2} hover:${text1}`}`}
                        >
                            {v === 'overview' ? '개요' : v === 'expenses' ? '지출 분석' : '거래 내역'}
                        </button>
                    ))}
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    { label: '이번 달 수입', value: currentMonth.income, icon: TrendingUp, color: 'text-green-400', bg: 'bg-green-500/10', growth: `+${incomeGrowth}%` },
                    { label: '이번 달 지출', value: currentMonth.expense, icon: TrendingDown, color: 'text-red-400', bg: 'bg-red-500/10', growth: '-2.3%' },
                    { label: '순이익 (당월)', value: currentMonth.balance, icon: PiggyBank, color: 'text-indigo-400', bg: 'bg-indigo-500/10', growth: '' },
                    { label: '저축률', value: null, icon: DollarSign, color: 'text-amber-400', bg: 'bg-amber-500/10', growth: '', text: `${savingsRate}%` },
                ].map((kpi, i) => (
                    <div key={i} className={`rounded-xl border p-4 ${bg} ${border} transition-all hover:scale-[1.02]`}>
                        <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs ${text2}`}>{kpi.label}</span>
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${kpi.bg}`}>
                                <kpi.icon className={`w-3.5 h-3.5 ${kpi.color}`} />
                            </div>
                        </div>
                        <div className={`text-lg font-bold ${text1}`}>
                            {kpi.text || fmt(kpi.value!)}
                        </div>
                        {kpi.growth && (
                            <span className={`text-[10px] ${kpi.growth.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
                                {kpi.growth} vs 전월
                            </span>
                        )}
                    </div>
                ))}
            </div>

            {/* Overview View */}
            {view === 'overview' && (
                <div className="grid md:grid-cols-2 gap-4">
                    {/* Monthly Trends */}
                    <div className={`rounded-xl border p-5 ${bg} ${border}`}>
                        <h3 className={`text-sm font-bold flex items-center gap-2 mb-4 ${text1}`}>
                            <BarChart3 className="w-4 h-4 text-indigo-400" />
                            월별 수입/지출 추이
                        </h3>
                        <div className="flex items-center gap-4 mb-3">
                            <span className="flex items-center gap-1 text-[10px] text-green-400">
                                <span className="w-2 h-2 rounded bg-green-500" /> 수입
                            </span>
                            <span className="flex items-center gap-1 text-[10px] text-red-400">
                                <span className="w-2 h-2 rounded bg-red-500" /> 지출
                            </span>
                        </div>
                        {renderBars()}
                    </div>

                    {/* Category Pie */}
                    <div className={`rounded-xl border p-5 ${bg} ${border}`}>
                        <h3 className={`text-sm font-bold flex items-center gap-2 mb-4 ${text1}`}>
                            <PieChartIcon className="w-4 h-4 text-indigo-400" />
                            카테고리별 지출
                        </h3>
                        <div className="flex items-start gap-4">
                            {renderPie()}
                            <div className="flex-1 space-y-2 pt-2">
                                {CATEGORIES.map((c, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c.color }} />
                                            <span className={`text-xs ${text2}`}>{c.category}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`text-xs font-mono ${text1}`}>{fmt(c.amount)}</span>
                                            <span className={`text-[10px] ${text3}`}>{c.percentage}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Recent Transactions */}
                    <div className={`md:col-span-2 rounded-xl border p-5 ${bg} ${border}`}>
                        <h3 className={`text-sm font-bold flex items-center gap-2 mb-4 ${text1}`}>
                            <Receipt className="w-4 h-4 text-indigo-400" />
                            최근 거래
                        </h3>
                        <div className="space-y-2">
                            {TRANSACTIONS.slice(0, 5).map(tx => (
                                <div key={tx.id} className={`flex items-center justify-between py-2 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tx.type === '입금' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                                            {tx.type === '입금' ? <ArrowUpRight className="w-4 h-4 text-green-400" /> : <ArrowDownRight className="w-4 h-4 text-red-400" />}
                                        </div>
                                        <div>
                                            <p className={`text-sm font-medium ${text1}`}>{tx.description}</p>
                                            <p className={`text-[10px] ${text3}`}>{tx.date} · {tx.category}</p>
                                        </div>
                                    </div>
                                    <span className={`text-sm font-mono font-bold ${tx.type === '입금' ? 'text-green-400' : 'text-red-400'}`}>
                                        {tx.type === '입금' ? '+' : '-'}{fmt(tx.amount)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Expenses View */}
            {view === 'expenses' && (
                <div className="space-y-4">
                    <div className={`rounded-xl border p-5 ${bg} ${border}`}>
                        <h3 className={`text-sm font-bold mb-4 ${text1}`}>📊 카테고리별 상세 지출</h3>
                        <div className="space-y-3">
                            {CATEGORIES.map((c, i) => (
                                <div key={i}>
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                            <span className="w-3 h-3 rounded" style={{ backgroundColor: c.color }} />
                                            <span className={`text-sm ${text1}`}>{c.category}</span>
                                        </div>
                                        <span className={`text-sm font-mono ${text1}`}>{fmtFull(c.amount)}</span>
                                    </div>
                                    <div className={`w-full h-2 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
                                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${c.percentage}%`, backgroundColor: c.color }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-3">
                        {[
                            { label: '카드 지출', amount: 45000000, icon: CreditCard, color: 'text-purple-400' },
                            { label: '계좌 이체', amount: 85000000, icon: Receipt, color: 'text-blue-400' },
                            { label: '현금 지출', amount: 20000000, icon: Wallet, color: 'text-amber-400' },
                        ].map((item, i) => (
                            <div key={i} className={`rounded-xl border p-4 ${bg} ${border}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <item.icon className={`w-4 h-4 ${item.color}`} />
                                    <span className={`text-xs ${text2}`}>{item.label}</span>
                                </div>
                                <span className={`text-lg font-bold ${text1}`}>{fmt(item.amount)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Transactions View */}
            {view === 'transactions' && (
                <div className={`rounded-xl border ${bg} ${border}`}>
                    <div className={`p-4 border-b ${border}`}>
                        <h3 className={`text-sm font-bold ${text1}`}>📋 전체 거래 내역</h3>
                    </div>
                    <div className="divide-y divide-gray-800/30">
                        {TRANSACTIONS.map(tx => (
                            <div key={tx.id} className={`flex items-center justify-between p-4 hover:${isDark ? 'bg-white/5' : 'bg-gray-50'} transition-colors`}>
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tx.type === '입금' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                                        {tx.type === '입금'
                                            ? <ArrowUpRight className="w-5 h-5 text-green-400" />
                                            : <ArrowDownRight className="w-5 h-5 text-red-400" />}
                                    </div>
                                    <div>
                                        <p className={`text-sm font-medium ${text1}`}>{tx.description}</p>
                                        <p className={`text-xs ${text3}`}>{tx.date} · {tx.category}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className={`text-sm font-mono font-bold ${tx.type === '입금' ? 'text-green-400' : 'text-red-400'}`}>
                                        {tx.type === '입금' ? '+' : '-'}{fmtFull(tx.amount)}
                                    </p>
                                    <p className={`text-[10px] ${text3}`}>{tx.type}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
